import React from 'react';
import { HorizontalBar } from 'react-chartjs-2';
import { RemoveYear } from '../../../common/date';

// Draws each bar's value at the end inside the (horizontal) bar, in white unless the dataset sets valueColor.
const barValuePlugin = {
    afterDatasetsDraw: function(chart){
        const ctx = chart.ctx;
        ctx.save();
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        chart.data.datasets.forEach((dataset, i) => {
            if (!chart.isDatasetVisible(i)) return;
            ctx.fillStyle = dataset.valueColor || '#fff';
            chart.getDatasetMeta(i).data.forEach((bar, index) => {
                const value = dataset.data[index];
                const text = value + (dataset.unit || '');
                const { x, y, base } = bar._model;
                // skip empty bars and bars too short to fit the text
                if (!Number(value) || Math.abs(x - base) < ctx.measureText(text).width + 8) return;
                ctx.fillText(text, x - 4, y);
            });
        });
        ctx.restore();
    }
};

// Bars get a full gap between date rows but only half a gap at the top and bottom edges;
// shrink the date axis by that half gap at each end so all gaps are even.
const evenEdgeGapsPlugin = {
    afterLayout: function(chart){
        const scale = chart.scales['y-axis-0'];
        const count = chart.data.labels.length;
        if (!scale || !count) return;
        const gap = 1 - scale.options.categoryPercentage;
        const pad = (scale.bottom - scale.top) * gap / (2 * (count + gap));
        scale.top += pad;
        scale.bottom -= pad;
        scale._configure();
    }
};

export default function FarmGraph({
    farm,
    weeklyData,
    onFarmDetails,
    isBroiler
}){
    const labels = [];
    const prodGraphData = [];
    const feedGraphData = [];
    const bodyWeightGraphData = [];
    const fcrGraphData = [];
    const {id} = farm;

    // IIFE
    (()=>{

        let IsNoweeklyData = false
        Object.keys(weeklyData).map(key=>{
            if (weeklyData[key] && weeklyData[key].date) {
                labels.push(RemoveYear(weeklyData[key].date));
            } else {
                IsNoweeklyData = true
            }
            if (weeklyData[key] && weeklyData[key].produtionPercentage) {
                var prodPer = (weeklyData[key].produtionPercentage % 1 === 0) ? weeklyData[key].produtionPercentage : weeklyData[key].produtionPercentage.toFixed(1);
                prodGraphData.push(prodPer);
            } else {
                prodGraphData.push(0);
            }
            if (weeklyData[key] && weeklyData[key].feedIntake) {
                var feedPB = (weeklyData[key].feedIntakePerGram % 1 === 0) ? weeklyData[key].feedIntakePerGram : weeklyData[key].feedIntakePerGram.toFixed(1);
                feedGraphData.push(feedPB);
            } else {
                feedGraphData.push(0);
            }
            if (weeklyData[key] && weeklyData[key].bodyWeight) {
                bodyWeightGraphData.push(weeklyData[key].bodyWeight.toFixed(0));
            } else {
                bodyWeightGraphData.push(0);
            }
            if (weeklyData[key] && weeklyData[key].fcr) {
                fcrGraphData.push(weeklyData[key].fcr.toFixed(3));
            } else {
                fcrGraphData.push(0);
            }
            return true;
        });

        if(IsNoweeklyData) {
            // default last week
            let lastWeek = Array.from({length: 7}, (_, i) => {
                const d = new Date();
                d.setDate(d.getDate() - i);
                return `${d.getMonth() + 1}/${d.getDate()}`;
              }).reverse();

            labels.push(...lastWeek);
        }
    })();

    // Broiler farms have no egg production - trend body weight & FCR instead.
    // Body weight (in grams) and FCR are on very different scales, so FCR gets
    // its own axis on the top.
    const ChartData = isBroiler ? {
        labels:labels,
        datasets: [
            {
                label: "Body Weight (g)",
                backgroundColor: '#1B3A56',
                data:bodyWeightGraphData,
                unit: 'g',
                borderWidth: 0,
                xAxisID: 'bodyWeight'
            },
            {
                label: "FCR",
                backgroundColor: '#F3B56B',
                data:fcrGraphData,
                borderWidth: 0,
                xAxisID: 'fcr'
            }
        ]
    } : {
        labels:labels,
        datasets: [
            {
                label: "Production %",
                backgroundColor: '#1B3A56',
                data:prodGraphData,
                unit: '%',
                borderWidth: 0
            },
            {
                label: "Feed Per Bird (g)",
                backgroundColor: '#F3B56B',
                data:feedGraphData,
                unit: 'g',
                valueColor: '#2c3e50',
                borderWidth: 0,
                stack: 'Stack 1'
            }
        ]
    }
        const ChartOptions = {
            responsive: true ,
            legend: {
             display: true
            },
            onClick:function(e){
                var thisSrc    = this.chart.getElementAtEvent(e)[0];
                if(thisSrc){
                    var clickedEle = thisSrc._index;
                    if(clickedEle !== undefined){
                        var cFarmData = {};
                        cFarmData[id] = weeklyData[clickedEle+1]; // farm weekly data index starting from 1
                        onFarmDetails(farm,cFarmData);
                    }
                }
            },
            scales: {
                xAxes: isBroiler ?
                [
                    {
                        id: 'bodyWeight',
                        position: 'bottom',
                        ticks:{
                            min: 0,
                        }
                    },
                    {
                        id: 'fcr',
                        position: 'top',
                        ticks:{
                            min: 0,
                        },
                        gridLines: {
                            drawOnChartArea: false
                        }
                    }
                ]
                :
                [
                    {
                        ticks:{
                            min: 0,
                        }
                      }
                ],
                // bars of a date stay together; categoryPercentage leaves an equal gap between date rows
                yAxes: [
                    {
                        barPercentage: 0.99,
                        categoryPercentage: 0.8,
                    }
                ]
              },
          }

    return(
            <HorizontalBar options={ChartOptions} data={ChartData} plugins={[evenEdgeGapsPlugin, barValuePlugin]} width={300} height={290}/>
    )
};
